import React, { useState } from 'react';
import { formatCurrency, validatePaymentAmount, PAYMENT_TYPES, PAYMENT_TYPE_LABELS, SPLIT_AT_TILL_TYPES } from '../utils/checkoutCalculations';
import klarnaLogo from '../../../assets/images/klarna-logo.png';
import clearpayLogo from '../../../assets/images/clearpay-logo.png';
import SplitAtTillCard, { useSelfEmployedOnBill } from '../SplitAtTillCard';
import toast from '../../shared/Toast';
import { tellIntro } from '../../onboarding/bailey-intro/introStage';

// Payment method icon components
const CashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const CardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);
const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const ReaderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="18" rx="3" /><line x1="8" y1="15" x2="16" y2="15" /><circle cx="12" cy="10" r="2" />
  </svg>
);
const TapToPayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="2" width="12" height="20" rx="2" /><circle cx="12" cy="18" r="0.5" fill="currentColor" />
    <path d="M2 8a3 3 0 0 1 0 4" /><path d="M4 6a5 5 0 0 1 0 8" />
  </svg>
);
const VoucherIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
    <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
  </svg>
);
const KlarnaIcon = () => (
  <img src={klarnaLogo} alt="Klarna" style={{ width: 20, height: 20, objectFit: 'contain' }} />
);
const ClearpayIcon = () => (
  <img src={clearpayLogo} alt="Clearpay" style={{ width: 20, height: 20, objectFit: 'contain' }} />
);
const OtherIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);
const LoyaltyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const PAYMENT_METHOD_CONFIG = [
  { type: PAYMENT_TYPES.CARD_READER, key: 'beuCardReaders', label: 'Card Reader', icon: ReaderIcon, color: '#29282D' },
  { type: PAYMENT_TYPES.TAP_TO_PAY, key: 'tapToPay', label: 'Tap to Pay', icon: TapToPayIcon, color: '#000000', requiresTapToPay: true },
  { type: PAYMENT_TYPES.CARD_ON_FILE, key: 'cardOnFile', label: 'Card on File', icon: CardIcon, color: '#6366f1' },
  { type: PAYMENT_TYPES.PAYMENT_LINK, key: 'paymentLink', label: 'Payment Link', icon: LinkIcon, color: '#007EA0' },
  { type: PAYMENT_TYPES.KLARNA, key: 'klarna', label: 'Klarna', icon: KlarnaIcon, color: '#FFB3C7', iconColor: '#1a1a1a' },
  { type: PAYMENT_TYPES.CLEARPAY, key: 'clearPay', label: 'Clearpay', icon: ClearpayIcon, color: '#B2FCE4', iconColor: '#1a1a1a' },
  { type: PAYMENT_TYPES.VOUCHER, key: 'vouchers', label: 'Voucher', icon: VoucherIcon, color: '#f59e0b' },
  { type: PAYMENT_TYPES.LOYALTY_REWARD, key: 'loyalty', label: 'Loyalty Reward', icon: LoyaltyIcon, color: '#007EA0', requiresLoyaltyReward: true },
  { type: PAYMENT_TYPES.CASH, key: 'cash', label: 'Cash', icon: CashIcon, color: '#34C759' },
  { type: PAYMENT_TYPES.CARD, key: 'card', label: 'Card', icon: CardIcon, color: '#3b82f6' },
  { type: PAYMENT_TYPES.OTHER, key: 'other', label: 'Other', icon: OtherIcon, color: '#8e8e93' },
];

/**
 * PaymentScreen
 * Amount selection and payment method grid.
 * Delegates actual payment processing to the paymentMethods hook.
 */
const PaymentScreen = ({ state, actions, dispatch, onBack, paymentMethods: pmHook, tapToPay, practice = false }) => {
  const [amountError, setAmountError] = useState(null);
  const realSplits = useSelfEmployedOnBill(state.lineItems);
  // In Bailey's welcome, Bobbi (the first example colleague) is self-employed.
  const splitsOnBill = realSplits || (practice && (state.lineItems || []).some((i) => i.staffId === 'demo-staff-1'));
  const currencySymbol = state.currencySymbol || '£';
  const fmt = (amount) => formatCurrency(amount, currencySymbol);

  const paymentAmount = state.amountMode === 'balance'
    ? state.balanceRemaining
    : parseFloat(state.customAmount) || 0;

  // Loyalty: show the button whenever this client holds a card, so staff can
  // see the reward exists rather than wondering where the option went. It is
  // only clickable once there is actually a reward to redeem.
  const loyaltyCards = state.loyaltyRewards || [];
  const hasRedeemableReward = loyaltyCards.some((c) => (c.rewardsAvailable || 0) > 0);

  // Progress for the greyed-out state, so staff can see how close the client
  // is and mention it. Read `progress`, never `filled`: filled is cumulative
  // across cycles, while progress is this cycle and already reads full on a
  // card with an unclaimed reward, so the till matches the client's app.
  // pointsTarget comes back in stamps mode too, so switch on `mode`.
  const loyaltyProgress = (() => {
    const card = loyaltyCards[0];
    if (!card) return null;
    const isPoints = card.mode === 'points';
    const target = isPoints ? card.pointsTarget : card.stampsRequired;
    if (!target) return null;
    const current = isPoints
      ? (card.balance != null ? card.balance : card.progress)
      : card.progress;
    if (current == null) return null;
    return `${current.toLocaleString()} of ${target.toLocaleString()}`;
  })();

  const enabledMethods = PAYMENT_METHOD_CONFIG.filter(method => {
    // Loyalty reward surfaces whenever the client holds a card, independent of
    // the salon's payment-method toggles.
    if (method.requiresLoyaltyReward) return loyaltyCards.length > 0;
    const methods = state.paymentMethods;
    if (!methods || Object.keys(methods).length === 0) {
      // Default state — only hide methods that require runtime checks
      if (method.requiresTapToPay) return false;
      return true;
    }
    // Hide card on file if no saved card
    if (method.type === PAYMENT_TYPES.CARD_ON_FILE && !pmHook?.paymentMethodInfo) return false;
    // Hide Tap to Pay unless BOTH salon enabled it AND device supports it
    if (method.requiresTapToPay && !tapToPay?.isAvailable) return false;
    return methods[method.key] !== false;
  });

  // Card on file label
  const cardOnFileLabel = pmHook?.paymentMethodInfo?.card
    ? `Card ••••${pmHook.paymentMethodInfo.card.last4}`
    : pmHook?.paymentMethodInfo?.link
    ? 'Card on File (Link)'
    : 'Card on File';

  // Card reader label - show reader name or count
  const readerCount = pmHook?.cardReaderList?.length || 0;
  const cardReaderLabel = readerCount === 1
    ? (pmHook.cardReaderList[0].label || 'Card Reader')
    : readerCount > 1
    ? `Card Reader (${readerCount})`
    : 'Card Reader';

  const handleSelectMethod = async (method) => {
    // Practice checkout: look, don't charge.
    if (practice) {
      toast.info("Just a look for now. Nothing is charged in the tour.");
      tellIntro('methodTapped', { type: method.type });
      return;
    }
    // Loyalty reward isn't an amount-based payment — it comps a chosen eligible
    // line — so route to its picker before the amount validation runs.
    if (method.type === PAYMENT_TYPES.LOYALTY_REWARD) {
      actions.setScreen('loyalty');
      return;
    }

    const validation = validatePaymentAmount(paymentAmount, state.balanceRemaining);
    if (!validation.valid) {
      setAmountError(validation.error);
      return;
    }
    setAmountError(null);
    const amount = validation.amount;

    switch (method.type) {
      case PAYMENT_TYPES.CASH:
      case PAYMENT_TYPES.CARD:
      case PAYMENT_TYPES.OTHER:
        pmHook.processSimplePayment(method.type, amount, method.label);
        if (state.balanceRemaining - amount > 0.01) {
          actions.setScreen('summary');
        }
        break;

      case PAYMENT_TYPES.PAYMENT_LINK:
        await pmHook.processPaymentLink(amount);
        break;

      case PAYMENT_TYPES.KLARNA:
        await pmHook.processKlarna(amount);
        break;

      case PAYMENT_TYPES.CLEARPAY:
        await pmHook.processClearpay(amount);
        break;

      case PAYMENT_TYPES.CARD_ON_FILE:
        await pmHook.processCardOnFile(amount);
        break;

      case PAYMENT_TYPES.CARD_READER:
        actions.setScreen('cardReader');
        break;

      case PAYMENT_TYPES.TAP_TO_PAY:
        actions.setScreen('tapToPay');
        break;

      case PAYMENT_TYPES.VOUCHER:
        actions.setScreen('voucher');
        break;

      default:
        break;
    }
  };

  return (
    <div className="co-payment">
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Payment</h2>
      </div>

      <div className="co-section">
        <div className="co-section-title">Amount</div>
        <div className="co-amount-toggle">
          <button
            className={`co-amount-option ${state.amountMode === 'balance' ? 'co-amount-option--active' : ''}`}
            onClick={() => { actions.setAmountMode('balance'); setAmountError(null); }}
          >
            <span className="co-amount-option-label">Balance remaining</span>
            <span className="co-amount-option-value">{fmt(state.balanceRemaining)}</span>
          </button>
          <button
            className={`co-amount-option ${state.amountMode === 'custom' ? 'co-amount-option--active' : ''}`}
            onClick={() => { actions.setAmountMode('custom'); setAmountError(null); }}
          >
            <span className="co-amount-option-label">Custom amount</span>
          </button>
        </div>

        {state.amountMode === 'custom' && (
          <div className="co-custom-amount">
            <span className="co-currency-symbol">{currencySymbol}</span>
            <input
              type="number"
              className="co-amount-input"
              placeholder="0.00"
              step="0.01"
              min="0.01"
              max={state.balanceRemaining}
              value={state.customAmount}
              onChange={e => { actions.setCustomAmount(e.target.value); setAmountError(null); }}
              autoFocus
            />
          </div>
        )}

        {amountError && <p className="co-error-msg">{amountError}</p>}
      </div>

      <div className="co-section">
        <SplitAtTillCard visible={splitsOnBill} practice={practice} />
        <div className="co-section-title">Payment Method</div>
        <div className="co-payment-grid">
          {enabledMethods.map(method => {
            const Icon = method.icon;
            // The client holds a card but has not earned a reward yet: keep the
            // tile visible so staff can see the scheme is running, but greyed
            // out and unclickable.
            const noRewardYet = method.requiresLoyaltyReward && !hasRedeemableReward;
            const label = method.type === PAYMENT_TYPES.CARD_ON_FILE ? cardOnFileLabel
              : method.type === PAYMENT_TYPES.CARD_READER ? cardReaderLabel
              : method.label;
            return (
              <button
                key={method.type}
                className="co-payment-tile"
                onClick={() => handleSelectMethod(method)}
                disabled={state.isProcessingPayment || noRewardYet}
                title={noRewardYet ? 'No reward ready to redeem yet' : undefined}
              >
                <div className="co-payment-tile-icon" style={{ backgroundColor: method.color, color: method.iconColor || '#fff' }}>
                  <Icon />
                </div>
                {noRewardYet ? (
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
                    <span className="co-payment-tile-label">{label}</span>
                    <span style={{ fontSize: 11, color: '#8e8e93' }}>
                      {loyaltyProgress || 'No reward yet'}
                    </span>
                  </span>
                ) : splitsOnBill && SPLIT_AT_TILL_TYPES.includes(method.type) ? (
                  <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
                    <span className="co-payment-tile-label">{label}</span>
                    <span className="co-payment-tile-split">Splits at till</span>
                  </span>
                ) : (
                  <span className="co-payment-tile-label">{label}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {state.isProcessingPayment && (
        <div className="co-loading" style={{ padding: '24px 0' }}>
          <div className="co-spinner" />
        </div>
      )}
    </div>
  );
};

export default PaymentScreen;
